/**
 * Einen Schritt der Abwicklung quittieren – das eine Formular der Abwicklung.
 *
 * Der rote Knopf auf der Bestellkarte nennt den nächsten Schritt beim Namen
 * und öffnet diesen Dialog: Datum (heute), Person (der Angemeldete), Dokument
 * (Pflicht, wo ein Schritt einen Beleg hat – Bestellung, AB, unterfertigte
 * AB, Lieferschein, Übergabeprotokoll), Notiz. Ab „AB retour" kommt der
 * Liefertermin dazu – meist als Kalenderwoche aus der unterfertigten AB;
 * „Termin bestätigt" verlangt das Datum.
 *
 * Vorgeschlagen werden die nächsten zwei Schritte; wer weiter springt, holt
 * die übrigen bewusst hervor und begründet, was dazwischen entfällt. Das
 * Aufmaß gilt auf Wunsch für alle Bestellungen des Auftrags – es findet in
 * der Regel einmal je Baustelle statt.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer, Pflicht, PflichtLegende } from '../komponenten/Modal';
import type { AngebotAntwort, Bestellung } from '../typen';

export function SchrittModal({
  bestellung,
  angebotName,
  auftragFehlt,
  onSchliessen,
  onFertig,
}: {
  bestellung: Bestellung;
  angebotName: string;
  /** Ohne Auftragshöhe lässt sich kein Schritt quittieren. */
  auftragFehlt: boolean;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen, nutzer } = useKora();
  const meta = useMeta();
  const { schritte, phasen, dokumente, dokumentArten } = meta.abwicklung;

  const kommende = schritte.filter(
    (s) => schritte.findIndex((x) => x.key === s.key) > bestellung.schrittIndex,
  );
  const naechster = kommende[0];
  const [schritt, setSchritt] = useState(naechster?.key ?? '');
  /* Weitere Schritte sind versteckt, bis jemand sie bewusst hervorholt. */
  const [alleZeigen, setAlleZeigen] = useState(false);
  const [datum, setDatum] = useState(meta.heute);
  const [person, setPerson] = useState(nutzer?.name ?? '');
  const [dokument, setDokument] = useState(dokumente[naechster?.key ?? ''] ?? '');
  const [dokumentBeruehrt, setDokumentBeruehrt] = useState(false);
  const [notiz, setNotiz] = useState('');
  const [lieferDatum, setLieferDatum] = useState(bestellung.lieferDatum ?? '');
  const [lieferWoche, setLieferWoche] = useState(bestellung.lieferWoche ?? '');
  const [alleBestellungen, setAlleBestellungen] = useState(bestellung.anzahl > 1);
  const [fehler, setFehler] = useState('');

  const ziel = schritte.find((s) => s.key === schritt);
  const zielIndex = schritte.findIndex((s) => s.key === schritt);
  const uebersprungen = ziel ? zielIndex - bestellung.schrittIndex - 1 : 0;
  const entfallende = uebersprungen > 0 ? kommende.slice(0, uebersprungen) : [];

  /* Dokument: Pflicht, wo der Schritt einen Beleg hat; sonst freiwillig ab „Bestellt". */
  const dokumentPflicht = !!dokumente[schritt];
  const dokumentFeld = dokumentPflicht || zielIndex >= schritte.findIndex((s) => s.key === 'bestellt');
  /*
   * Der Liefertermin steht in der unterfertigten AB – meist als Kalenderwoche.
   * Ab „AB retour" muss er da sein (Woche oder Datum), „Termin bestätigt"
   * verlangt das Datum. Beim Bestellen kennt ihn noch niemand.
   */
  const retourIndex = schritte.findIndex((s) => s.key === 'retour');
  const terminFeld = zielIndex >= retourIndex && zielIndex <= schritte.findIndex((s) => s.key === 'termin');
  const terminPflicht = terminFeld && !lieferDatum && !lieferWoche;
  const datumPflicht = schritt === 'termin';

  function schrittWaehlen(key: string) {
    setSchritt(key);
    setFehler('');
    /* Das natürliche Dokument des Schritts vorbelegen, solange niemand selbst gewählt hat. */
    if (!dokumentBeruehrt) setDokument(dokumente[key] ?? '');
  }

  const mutation = useMutation({
    mutationFn: () =>
      api.sende<AngebotAntwort>(`/abwicklung/bestellungen/${bestellung.id}/schritt`, {
        schritt,
        datum,
        person,
        dokument: dokumentFeld ? dokument : '',
        notiz: notiz.trim(),
        lieferDatum: terminFeld ? lieferDatum || null : undefined,
        lieferWoche: terminFeld ? lieferWoche || null : undefined,
        alleBestellungen: schritt === 'aufmass' && bestellung.anzahl > 1 ? alleBestellungen : undefined,
      }),
    onSuccess: (antwort) => {
      onFertig();
      if (antwort.toast) toastZeigen(antwort.toast);
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  function speichern() {
    if (!schritt) {
      setFehler('Bitte einen Schritt wählen.');
      return;
    }
    if (!datum) {
      setFehler('Bitte das Datum angeben.');
      return;
    }
    if (!person) {
      setFehler('Bitte angeben, wer den Schritt erledigt hat.');
      return;
    }
    if (uebersprungen > 0 && notiz.trim().length < 3) {
      setFehler(
        uebersprungen === 1
          ? 'Bitte begründen, warum der Schritt entfällt.'
          : `Bitte begründen, warum die ${uebersprungen} Schritte entfallen.`,
      );
      return;
    }
    if (terminPflicht) {
      setFehler('Mit der unterfertigten AB steht der Liefertermin fest – bitte die Kalenderwoche oder das Datum eintragen.');
      return;
    }
    if (datumPflicht && !lieferDatum) {
      setFehler('Ein bestätigter Termin braucht das Datum vom Lieferanten.');
      return;
    }
    if (dokumentPflicht && !dokument) {
      setFehler(`Bitte angeben, welches Dokument vorliegt – bei „${ziel?.lang}" in der Regel: ${dokumente[schritt]}.`);
      return;
    }
    mutation.mutate();
  }

  const gesperrt = auftragFehlt || kommende.length === 0;
  const pflichtFehlt = bestellung.fehlt.length > 0 && zielIndex >= schritte.findIndex((s) => s.key === 'bestellt');

  return (
    <Modal
      titel={naechster ? naechster.aktion : 'Schritt quittieren'}
      untertitel={`${angebotName} · Bestellung ${bestellung.nr}${bestellung.lieferant ? ` · ${bestellung.lieferant}` : ''} · steht auf „${bestellung.schritt === 'erteilt' ? 'Auftrag erteilt' : bestellung.schrittLang}"`}
      breite={560}
      fest={mutation.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={mutation.isPending}>
            {gesperrt ? 'Schließen' : 'Abbrechen'}
          </KnopfSekundaer>
          {!gesperrt && (
            <KnopfPrimaer onClick={speichern} disabled={mutation.isPending || !schritt || pflichtFehlt}>
              {mutation.isPending ? 'Wird gespeichert …' : ziel ? `„${ziel.aktion}" quittieren` : 'Quittieren'}
            </KnopfPrimaer>
          )}
        </>
      }
    >
      {auftragFehlt ? (
        <div className="modal-fehler">
          Die Auftragshöhe fehlt. Bitte zuerst rechts im Vorgang eintragen – ohne sie lässt sich
          kein Schritt quittieren.
        </div>
      ) : kommende.length === 0 ? (
        <div className="modal-hinweis">
          Die Bestellung steht im letzten Schritt. Die Abnahme des Auftrags läuft über „Auftrag
          abgenommen".
        </div>
      ) : (
        <>
          <div className="modal-feld">
            <span>Erledigt ist</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(alleZeigen ? kommende : kommende.slice(0, 2)).map((s) => {
                const phase = phasen.find((p) => p.key === s.phase);
                return (
                  <button
                    key={s.key}
                    type="button"
                    className={`modal-radio ${schritt === s.key ? 'aktiv' : ''}`}
                    onClick={() => schrittWaehlen(s.key)}
                  >
                    <span
                      className="modal-radio-punkt"
                      style={phase ? { borderColor: phase.farbe } : undefined}
                    />
                    <span className="modal-radio-text">
                      {s.aktion}
                      <span style={{ color: 'var(--eh-text-3)', fontWeight: 400 }}> · {phase?.label}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            {!alleZeigen && kommende.length > 2 && (
              <button type="button" className="modal-textlink" onClick={() => setAlleZeigen(true)}>
                Alle weiteren Schritte anzeigen
              </button>
            )}
            {/* Immer gelb mit blauer Schrift: eine Information, keine Warnung. */}
            {entfallende.length > 0 && (
              <div className="modal-info">
                {entfallende.length === 1 ? 'Entfällt' : 'Entfallen'}:{' '}
                {entfallende.map((s) => s.lang).join(', ')}. Das ist erlaubt – mit Begründung.
              </div>
            )}
          </div>

          {pflichtFehlt && (
            <div className="modal-fehler">
              Vorher fehlen Pflichtangaben an der Bestellung: {bestellung.fehlt.join(', ')}. Bitte
              über den Stift ergänzen – dann lässt sich „{ziel?.lang}" quittieren.
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label className="modal-feld" style={{ flex: '0 0 170px' }}>
              <span>Am</span>
              <input
                type="date"
                className="modal-eingabe"
                value={datum}
                max={meta.heute}
                onChange={(e) => {
                  setDatum(e.target.value);
                  setFehler('');
                }}
              />
            </label>
            <label className="modal-feld" style={{ flex: 1, minWidth: 180 }}>
              <span>Erledigt von</span>
              <select
                className="modal-eingabe"
                value={person}
                onChange={(e) => {
                  setPerson(e.target.value);
                  setFehler('');
                }}
              >
                {meta.team.map((t) => (
                  <option key={t.name} value={t.name}>
                    {t.anzeigename || t.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {terminFeld && (
            <div className="modal-feld">
              <span>
                Liefertermin laut AB
                {schritt === 'retour' ? ' · meist als Kalenderwoche' : ''}
                {terminFeld && (terminPflicht || datumPflicht || schritt === 'retour') ? <Pflicht /> : null}
              </span>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <label className="modal-feld" style={{ flex: 1, minWidth: 160, marginTop: 0 }}>
                  <span style={{ fontSize: 12 }}>Kalenderwoche</span>
                  <input
                    type="week"
                    className={`modal-eingabe ${fehler && terminPflicht ? 'modal-eingabe--fehler' : ''}`}
                    value={lieferWoche}
                    onChange={(e) => {
                      setLieferWoche(e.target.value);
                      setFehler('');
                    }}
                  />
                </label>
                <label className="modal-feld" style={{ flex: 1, minWidth: 160, marginTop: 0 }}>
                  <span style={{ fontSize: 12 }}>Bestätigtes Datum{datumPflicht ? <Pflicht /> : null}</span>
                  <input
                    type="date"
                    className={`modal-eingabe ${fehler && (terminPflicht || (datumPflicht && !lieferDatum)) ? 'modal-eingabe--fehler' : ''}`}
                    value={lieferDatum}
                    onChange={(e) => {
                      setLieferDatum(e.target.value);
                      setFehler('');
                    }}
                  />
                </label>
              </div>
            </div>
          )}

          {dokumentFeld && (
            <label className="modal-feld">
              <span>Dokument liegt vor{dokumentPflicht ? <Pflicht /> : ' · optional'}</span>
              <select
                className={`modal-eingabe ${fehler && dokumentPflicht && !dokument ? 'modal-eingabe--fehler' : ''}`}
                value={dokument}
                onChange={(e) => {
                  setDokument(e.target.value);
                  setDokumentBeruehrt(true);
                  setFehler('');
                }}
              >
                <option value="">{dokumentPflicht ? 'bitte wählen …' : 'kein Dokument'}</option>
                {dokumentArten.map((d) => (
                  <option key={d} value={d}>
                    {d}
                    {dokumente[schritt] === d ? ' · üblich bei diesem Schritt' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}

          {schritt === 'aufmass' && bestellung.anzahl > 1 && (
            <label className="modal-feld modal-feld--zeile">
              <input
                type="checkbox"
                checked={alleBestellungen}
                onChange={(e) => setAlleBestellungen(e.target.checked)}
              />
              <span>
                Für alle {bestellung.anzahl} Bestellungen dieses Auftrags übernehmen – das Aufmaß
                findet in der Regel einmal je Baustelle statt.
              </span>
            </label>
          )}

          <label className="modal-feld">
            <span>
              {uebersprungen > 0 ? <>Warum entfallen die Schritte?<Pflicht /></> : 'Notiz · optional'}
            </span>
            <textarea
              className={`modal-textarea ${fehler && uebersprungen > 0 && notiz.trim().length < 3 ? 'modal-eingabe--fehler' : ''}`}
              rows={2}
              placeholder={
                uebersprungen > 0
                  ? 'z. B. Lieferant bestätigt per Mail, keine AB nötig'
                  : 'Was ist zu wissen?'
              }
              value={notiz}
              onChange={(e) => {
                setNotiz(e.target.value);
                setFehler('');
              }}
            />
          </label>
        </>
      )}

      {!gesperrt && <PflichtLegende />}
      {fehler && <div className="modal-fehler">{fehler}</div>}
    </Modal>
  );
}
